import type { RecoProductCardFragment } from '@packages/gql/generated/shopping/graphql';
import type { ProductCardV2EnergyEfficiencyFlagsProps } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlags.client';
import type { PriceDiscount } from '@packages/modules/src/ProductCardV2/ProductCardV2.Price.client';
import type { ProductCardV2TireEfficiencyFlagsProps } from '@packages/modules/src/ProductCardV2/ProductCardV2.TireEfficiencyFlags.client';
import type { useIntl } from 'react-intl';
import type { ProductCardV2EnergyEfficiencyFlagsLegacyProps } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlagsLegacy';
import { TireEfficiencyFlagSpecificProps } from '@packages/shared/src/ui/TireEfficiencyFlag/TireEfficiencyFlag';
import { getDiscount } from './getDiscount/getDiscount';
import {
  getEnergyEfficiencyFlags,
  getLegacyEnergyEfficiencyFlags,
} from './getEnergyEfficiencyFlags/getEnergyEfficiencyFlags';
import {
  getLegacyTireEfficiencyFlags,
  getTireEfficiencyFlags,
} from './getTireEfficiencyFlags/getTireEfficiencyFlags';

export type FormattedProductProps = {
  productLink: string;
  productId: string;
  imageUrl: string;
  price: {
    currency: 'EUR' | 'CHF';
    isStartPrice?: boolean | null;
    currentPrice: number;
    discount?: PriceDiscount | null;
    unitPackage?: {
      price: number;
      amount: number;
      unit: string;
    };
  };
  energyEfficiencyFlags?: ProductCardV2EnergyEfficiencyFlagsProps['flags'];
  tireEfficiencyFlags?: ProductCardV2TireEfficiencyFlagsProps['flags'];
  legacyEfficiencyFlags?: ProductCardV2EnergyEfficiencyFlagsLegacyProps;
};

export const getFormattedProduct = (
  product: NonNullable<RecoProductCardFragment['product']>,
  tracking: NonNullable<RecoProductCardFragment['tracking']>,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): FormattedProductProps => {
  const { downloads, price, powerEfficiencyFlags, tireEfficiency } = product;
  const productId = product.sku;
  const imageUrl = product.media.find((ele) => ele.type === 'IMAGE')?.url;
  const criteoLink = tracking.trackingUrls.find((ele) => ele.purpose === 'ProductPage')?.url;

  const discount = getDiscount(price);
  const tireEfficiencyFlags = getTireEfficiencyFlags(tireEfficiency, formatMessage);
  const energyEfficiencyFlags = getEnergyEfficiencyFlags(powerEfficiencyFlags);

  const datasheets = downloads
    .filter((download) => download.type === 'PRODUCT_DATASHEET')
    .map((download) => ({
      url: download.link,
      name: 'Produktdatenblatt',
    }));

  datasheets.forEach((datasheet, idx) => {
    energyEfficiencyFlags.length > 0 &&
      energyEfficiencyFlags[idx] &&
      (energyEfficiencyFlags[idx].datasheet = datasheet);
  });

  // Legacy flags can be removed when EEK flags can be assigned to corresponding datasheet
  const legacyTireEfficiencyFlags = getLegacyTireEfficiencyFlags(tireEfficiency, formatMessage);
  const legacyEnergyEfficiencyFlags = getLegacyEnergyEfficiencyFlags(powerEfficiencyFlags);

  const legacyEfficiencyFlags: ProductCardV2EnergyEfficiencyFlagsLegacyProps = {
    flags: [...legacyTireEfficiencyFlags, ...legacyEnergyEfficiencyFlags],
    datasheets,
  };

  const formattedProduct: FormattedProductProps = {
    productLink: criteoLink || product.link,
    productId,
    imageUrl: imageUrl ? `${imageUrl}?$PL_square_hq$` : '#',
    price: {
      currency: price.currency,
      currentPrice: price.value,
      isStartPrice: price.isStartPrice,
      discount,
    },
    energyEfficiencyFlags,
    tireEfficiencyFlags,
    legacyEfficiencyFlags,
  };

  return formattedProduct;
};
